import { inject, Injectable } from '@angular/core';

import { DealerProvider } from '../../../../../auth/dealer/dealer.provider';
import { normalizeProvinceCode } from '../../../../utils/province-code';
import { CustomerAddress, CustomerMainType, MainAddress } from '../../../model/customer-information.model';
import { ExtendedCustomer } from '../../../model/extended-customer.model';
import {
  fromGcdmNationalIdDocumentType,
  fromGcdmSalutation,
  fromGcdmTitle,
  fromGcdmVatType,
} from '../model/gcdm-code.mapping';
import {
  GcdmAddress,
  GcdmAddressStatus,
  GcdmBusinessPartner,
  GcdmCommunicationStatus,
  GcdmEmailCommunicationType,
  GcdmPartnerCategory,
  GcdmPersonalIdentification,
  GcdmPhoneCommunicationType,
  GcdmTaxData,
} from '../model/response.model';

/** Entries with no timestamp sort oldest, as in RT CRM's mapping. */
const OLDEST = '1900-01-01';

/**
 * Maps a GCDM business partner to CDF's comparison model for the profile sync dialog.
 *
 * Unlike the ESO CRM flow this needs no second request: the business partner returned by the
 * search already contains every field CDF compares.
 *
 * The selection rules (newest communication wins, address matched on `preferredFor`, tax id and
 * VAT number read from *different* entries) mirror RT CRM's own GCDM mapping. Diverging from them
 * would make CDF show differences that only exist in CDF's interpretation of the same record.
 *
 */
@Injectable({
  providedIn: 'root',
})
export class GcdmExtendedCustomerViewModelMapper {
  private readonly dealerProvider = inject(DealerProvider);
  /** Markets where a foreign national's id is held as a passport id rather than a person id. */
  private static readonly PassportIdMarkets = new Set(['ES', 'PT']);

  mapToExtendedCustomer(data: GcdmBusinessPartner): ExtendedCustomer {
    const identification = this.getIdentification(data);

    return {
      gcid: data.gcid,
      firstName: data.givenName,
      middleName: data.middleName,
      lastName: data.surname,
      nameInitials: data.initialsName,
      // `additionalFamilyName`, not `nameSuffixGeneration`: the latter is GCDM's JR/SR enum,
      // while Retail CRM's free-text suffix maps to the former.
      suffix: data.additionalFamilyName,
      // GCDM may return a full timestamp; Retail CRM stores a plain date.
      birthdate: data.birthday?.slice(0, 10),
      salutation: fromGcdmSalutation(data.salutation),
      title: fromGcdmTitle(data.title),
      // GCDM upper-cases the language, CDF and Retail CRM hold it lower-cased.
      // documentLanguage, not correspondLanguageISO: the latter is GCDM's spoken language.
      correspondenceLanguage: data.documentLanguage?.toLowerCase(),
      placeOfBirth: data.birthAddress?.city,
      nationality: data.nationalityISO,
      selfEmployed: data.commerciallySelfEmployed,
      isInsolvent: data.businessData?.insolvent,
      emailPrivate: this.getEmail(data, GcdmCommunicationStatus.PRIVATE),
      emailBusiness: this.getEmail(data, GcdmCommunicationStatus.BUSINESS),
      phonePrivate: this.getPhone(data, GcdmPhoneCommunicationType.TEL, GcdmCommunicationStatus.PRIVATE),
      phoneBusiness: this.getPhone(data, GcdmPhoneCommunicationType.TEL, GcdmCommunicationStatus.BUSINESS),
      mobilePrivate: this.getPhone(data, GcdmPhoneCommunicationType.MOB, GcdmCommunicationStatus.PRIVATE),
      mobileBusiness: this.getPhone(data, GcdmPhoneCommunicationType.MOB, GcdmCommunicationStatus.BUSINESS),
      privateAddress: this.mapAddress(this.getAddress(data, GcdmAddressStatus.PRIVATE)),
      businessAddress: this.mapAddress(this.getAddress(data, GcdmAddressStatus.BUSINESS)),
      mainAddress: this.getMainAddress(data),
      taxNumber: this.getTaxIdData(data)?.taxId,
      taxNumberCountry: this.getTaxIdData(data)?.taxCountryISO,
      vatGroup: fromGcdmVatType(this.getVatData(data)?.vatType),
      nationalId1: identification?.id,
      nationalId1DocumentType: fromGcdmNationalIdDocumentType(identification?.type),
      // CDF only ever writes persons; an organisation here was created elsewhere.
      customerMainType:
        data.partnerCategory === GcdmPartnerCategory.ORGANISATION
          ? CustomerMainType.ORGANIZATION
          : CustomerMainType.INDIVIDUAL,
      // `description` has no GCDM counterpart in either direction; the container keeps Retail CRM's value.
    };
  }

  /**
   * GCDM allows several identifications and marks none of them preferred, while Retail CRM has a
   * single slot. ESO CRM does not take the first entry: it selects by type, and the type depends on
   * the market and on whether the customer is a foreign national.
   */
  private getIdentification(data: GcdmBusinessPartner): GcdmPersonalIdentification | undefined {
    const market = this.dealerProvider.activeCountry()?.toUpperCase();
    const nationality = data.nationalityISO?.toUpperCase();

    const isForeignNational = !!market && !!nationality && market !== nationality;
    const type =
      isForeignNational && GcdmExtendedCustomerViewModelMapper.PassportIdMarkets.has(market)
        ? 'PER_IDE_PASSPORT_ID'
        : 'PER_IDE_PERSON_ID';

    return (data.profile?.personalIdentifications?.personalIdentifications ?? []).find(
      (identification) => identification.type === type,
    );
  }

  private mapAddress(address: GcdmAddress | undefined): CustomerAddress | undefined {
    if (!address) {
      return undefined;
    }

    return {
      street: address.street,
      houseNumber: address.houseNumber,
      postalCode: address.postalCode,
      city: address.city,
      country: address.country,
      province: normalizeProvinceCode(address.regionCode, address.country),
      // Read back as well as written: an unmapped supplement would be shown as empty and
      // cleared in GCDM by the next save.
      addressLine1: address.strSuppl1,
      addressLine2: address.strSuppl2,
      postBox: address.postOfficeBox,
    };
  }

  /**
   * The address of the given category that is also preferred *for* it.
   *
   * No fallback to "any address with this status" on purpose: RT CRM applies the same strict
   * match, so a looser rule here would surface an address Retail CRM will never see.
   */
  private getAddress(data: GcdmBusinessPartner, status: GcdmAddressStatus): GcdmAddress | undefined {
    return (data.addresses?.addresses ?? []).find(
      (address) => address.addressStatus === status && address.preferredFor === status,
    );
  }

  /** GCDM marks exactly one address as globally `preferred`; its category is CDF's main address. */
  private getMainAddress(data: GcdmBusinessPartner): MainAddress | undefined {
    const preferred = (data.addresses?.addresses ?? []).find((address) => address.preferred);
    if (!preferred?.addressStatus) {
      return undefined;
    }
    return preferred.addressStatus === GcdmAddressStatus.BUSINESS ? MainAddress.BUSINESS : MainAddress.PRIVATE;
  }

  private getEmail(data: GcdmBusinessPartner, status: GcdmCommunicationStatus): string | undefined {
    const emails = (data.communications?.communicationEMails ?? []).filter(
      // EPOST entries live in the same array but are not email addresses.
      (email) => email.communicationType === GcdmEmailCommunicationType.EMAIL && email.communicationStatus === status,
    );
    return this.newest(emails)?.value;
  }

  private getPhone(
    data: GcdmBusinessPartner,
    type: GcdmPhoneCommunicationType,
    status: GcdmCommunicationStatus,
  ): string | undefined {
    const phones = (data.communications?.communicationPhones ?? []).filter(
      (phone) => phone.communicationType === type && phone.communicationStatus === status,
    );
    return this.newest(phones)?.value;
  }

  /**
   * GCDM allows several entries per type/status pair and marks none of them preferred, so
   * RT CRM takes the most recently updated one. CDF follows.
   */
  private newest<T extends { updatedAt?: string }>(entries: T[]): T | undefined {
    return entries.reduce<T | undefined>(
      (latest, entry) => (!latest || (entry.updatedAt ?? OLDEST) > (latest.updatedAt ?? OLDEST) ? entry : latest),
      undefined,
    );
  }

  /**
   * Tax id and VAT number can live in different `taxDatas` entries, so they are looked up
   * separately. The country-group rules (IT also accepting SM/VA, ES/AD, CH/LI, FR/MC) are not
   * applied here: they need the user's market, which only the backend knows.
   */
  private getTaxIdData(data: GcdmBusinessPartner): GcdmTaxData | undefined {
    return (data.taxDatas ?? []).find((taxData) => taxData.taxId);
  }

  private getVatData(data: GcdmBusinessPartner): GcdmTaxData | undefined {
    return (data.taxDatas ?? []).find((taxData) => taxData.vatRegNo);
  }
}
